import {
  GeneralSelect,
  DateInput,
  DateRange,
  Tooltip,
  MultiValue,
} from '@clickcease/cc-client-components-lib'
import { faFileArrowDown } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { fromUnixTime } from 'date-fns'
import { debounce } from 'lodash'
import React, { useMemo, useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import ReactToPrint from 'react-to-print'

import { useAppStore } from 'Stores/appStoreModuleFed'

import { STATUSES } from '../../../../../hooks/statuses/statuses.model'
import { useGetBMProtectionStatus } from '../../../../../hooks/statuses/useGetBMProtectionStatus'
import { useBMStore } from '../../../../../stores/useBMStore'
import { Domain } from 'Types/Domain'

import {
  DomainSelectWrapper,
  GeneralSelectWrapper,
  StatusWrapper,
  StatusText,
  GetProtectedButton,
  ReactToPrintArea,
  ReactToPrintButton,
  DateAndReactToPrintWrapper,
  SingleDomainDisplay,
} from './Header.styled'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

interface HeaderProps {
  locale?: string
  dateRange?: { from: number; to: number }
  onDateRangeSelected: (dateRange: DateRange | undefined) => void
  printRef?: React.MutableRefObject<null>
}

interface DomainOption {
  label: string
  value: number
}

export const Header: React.FC<HeaderProps> = ({
  locale,
  dateRange,
  onDateRangeSelected,
  printRef,
}) => {
  const { t } = useTranslation('botMitigation')

  const domains = useBMStore((state) => state.domains)
  const chosenDomains = useBMStore((state) => state.chosenDomains)
  const setChosenDomains = useBMStore((state) => state.setChosenDomains)
  const navigate = useNavigate()
  const { setCurrentDomain, setModalToShow } = useAppStore()

  const [selectedDomainOptions, setSelectedDomainOptions] = useState<DomainOption[]>(() => {
    if (!chosenDomains || chosenDomains.length === 0) {
      return [{ label: t('allDomains'), value: -1 }]
    }
    return chosenDomains.map((domain) => ({
      label: domain.address,
      value: domain.id,
    }))
  })

  const protectionStatuses = useGetBMProtectionStatus(domains)

  const protectionStatus = useMemo(() => {
    if (!chosenDomains || chosenDomains.length === 0) return undefined

    if (chosenDomains.length === 1) {
      return protectionStatuses[chosenDomains[0].address]
    }

    return undefined
  }, [chosenDomains, protectionStatuses])

  const debouncedSetChosenDomains = useRef(
    debounce((domains: Domain[] | null) => {
      setChosenDomains(domains)
    }, 1500)
  ).current

  useEffect(() => {
    return () => {
      debouncedSetChosenDomains.cancel()
    }
  }, [debouncedSetChosenDomains])

  const domainOptions = useMemo(() => {
    const domainsList =
      domains?.map((domain) => ({
        label: domain.address,
        value: domain.id,
      })) || []

    return domains && domains.length > 1
      ? [{ label: t('allDomains'), value: -1 }, ...domainsList]
      : domainsList
  }, [domains, t])

  const handleDomainChange = (newValue: MultiValue<DomainOption>) => {
    // Check if user is attempting to add a specific domain while "All Domains" is already selected
    const allDomainsOption = { label: t('allDomains'), value: -1 }
    const hasAllDomains = newValue.some((option) => option.value === -1)
    const wasAllDomainsAlreadySelected = selectedDomainOptions.some((option) => option.value === -1)

    if (wasAllDomainsAlreadySelected && hasAllDomains && newValue.length > 1) {
      const specificDomains = newValue.filter((option) => option.value !== -1)
      setSelectedDomainOptions(specificDomains)

      const selectedDomains = specificDomains
        .map((option) => domains.find((domain) => domain.id === option.value) || null)
        .filter(Boolean) as Domain[]

      debouncedSetChosenDomains(selectedDomains.length > 0 ? selectedDomains : null)
      return
    }

    if (hasAllDomains && !wasAllDomainsAlreadySelected) {
      setSelectedDomainOptions([allDomainsOption])
      debouncedSetChosenDomains(null)
      return
    }

    if (!newValue.length) {
      setSelectedDomainOptions([allDomainsOption])
      debouncedSetChosenDomains(null)
      return
    }

    setSelectedDomainOptions(newValue as DomainOption[])

    const selectedDomains = newValue
      .map((option) => domains.find((domain) => domain.id === option.value) || null)
      .filter(Boolean) as Domain[]

    debouncedSetChosenDomains(selectedDomains.length > 0 ? selectedDomains : null)
  }

  const dateRangeToDateFromUnix = dateRange
    ? { from: fromUnixTime(dateRange.from), to: fromUnixTime(dateRange.to) }
    : undefined

  const onClickHandler = () => {
    if (
      protectionStatus === STATUSES.notProtected.label &&
      chosenDomains?.length === 1 &&
      chosenDomains !== null
    ) {
      setCurrentDomain(chosenDomains[0])
      setModalToShow('bzSetup')
    } else if (
      protectionStatus === STATUSES.wordpressNotRecognized.label &&
      chosenDomains?.length === 1 &&
      chosenDomains !== null
    ) {
      setCurrentDomain(chosenDomains[0])
      setModalToShow('editDomainProducts')
    } else {
      navigate('/domain-management', { replace: true })
    }
  }

  return (
    <DomainSelectWrapper>
      <GeneralSelectWrapper>
        {domains?.length === 1 ? (
          <SingleDomainDisplay>
            <label>{t('domain')}</label>
            {chosenDomains?.[0]?.address}
          </SingleDomainDisplay>
        ) : (
          <GeneralSelect
            title={t('domain')}
            options={domainOptions}
            isMulti
            selectionLimit={15}
            value={selectedDomainOptions}
            onChange={handleDomainChange}
            isClearable
          />
        )}

        {protectionStatus && (
          <StatusWrapper>
            <FontAwesomeIcon
              icon={STATUSES[protectionStatus]?.icon}
              style={{ color: STATUSES[protectionStatus]?.color }}
              size={'lg'}
            />
            <StatusText>{t(`protectionStatus.${protectionStatus}`)}</StatusText>
            {protectionStatus !== STATUSES.protected.label && (
              <GetProtectedButton onClick={onClickHandler}>
                <label>{t('getProtected')}</label>
              </GetProtectedButton>
            )}
          </StatusWrapper>
        )}
      </GeneralSelectWrapper>
      <DateAndReactToPrintWrapper>
        <DateInput
          localeFormat={locale}
          onSelect={onDateRangeSelected}
          selectedDateRange={dateRangeToDateFromUnix}
        />
        {printRef && (
          <ReactToPrintArea>
            <ReactToPrint
              trigger={() => (
                <ReactToPrintButton>
                  <Tooltip
                    side="left"
                    tooltiptContent={t('SummaryWidget.download-whitelabel-report')}
                  >
                    <FontAwesomeIcon
                      icon={faFileArrowDown as IconProp}
                      size="lg"
                      style={{ width: '13px' }}
                    />
                  </Tooltip>
                </ReactToPrintButton>
              )}
              content={() => printRef.current}
            />
          </ReactToPrintArea>
        )}
      </DateAndReactToPrintWrapper>
    </DomainSelectWrapper>
  )
}
